import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, CardContent, Typography, Stack, Alert, Box, IconButton, Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  TipsAndUpdates as CoachingIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { notificationsApi } from '../api/services';

// ---------------------------------------------------------------------------
// Coaching insights card. The backend already computes alerts across stalled
// drivers, overdue items, carry-forward tails, and red projects — prior to
// this surface they only reached MCP callers. This card promotes them to a
// first-class glance on the ReflectionsPage close-day flow and on the main
// dashboard so an executive sees "here's what the system noticed about your
// week" in a predictable place.
// ---------------------------------------------------------------------------

interface CoachingInsight {
  title: string;
  message: string;
  entityType?: string;
  entityId?: string;
}

interface CoachingInsightsCardProps {
  /** Optional executive id when the current user is an EA acting on someone else's behalf. */
  forExecutiveUserId?: string;
  /** Optional heading — defaults to "Coaching insights". */
  title?: string;
  /** When set, clicking an insight navigates to the relevant screen. */
  clickable?: boolean;
}

function insightHref(entityType?: string, entityId?: string): string | null {
  if (!entityType || !entityId) return null;
  switch (entityType) {
    case 'Project': return `/projects/${entityId}`;
    case 'StrategicDriver': return `/drivers/${entityId}`;
    case 'WorkItem': return `/work-items?id=${entityId}`;
    case 'Decision': return `/decisions?id=${entityId}`;
    case 'Kpi':
    case 'StrategicKpi': return `/drivers`;
    default: return null;
  }
}

export default function CoachingInsightsCard({
  forExecutiveUserId,
  title = 'Coaching insights',
  clickable = true,
}: CoachingInsightsCardProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [insights, setInsights] = useState<CoachingInsight[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await notificationsApi.getCoachingInsights(forExecutiveUserId);
      setInsights(Array.isArray(result) ? result : []);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Coaching insights unavailable.');
    } finally {
      setLoading(false);
    }
  }, [forExecutiveUserId]);

  useEffect(() => { load(); }, [load]);

  // When there's nothing to say, stay silent — the card earns attention only
  // when there's a real nudge to show. Loading / error states still render so
  // the user isn't confused by an absent card they clicked in from MCP.
  if (!loading && !error && (!insights || insights.length === 0)) {
    return null;
  }

  return (
    <Card sx={{ bgcolor: 'background.paper', borderLeft: 3, borderColor: 'info.main', mb: 2 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <CoachingIcon sx={{ fontSize: 20, color: 'info.main' }} />
          <Typography variant="body2" fontWeight={600} sx={{ color: 'info.main' }}>
            {title}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Refresh insights" arrow>
            <IconButton size="small" onClick={load} disabled={loading} aria-label="Refresh">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {loading && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Checking your week&hellip;
          </Typography>
        )}

        {error && <Alert severity="warning" sx={{ mt: 0.5 }}>{error}</Alert>}

        {!loading && !error && insights && (
          <Stack spacing={1}>
            {insights.slice(0, 5).map((ins, idx) => {
              const href = clickable ? insightHref(ins.entityType, ins.entityId) : null;
              const body = (
                <Box
                  key={idx}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.info.main, 0.06),
                    cursor: href ? 'pointer' : 'default',
                    '&:hover': href ? { bgcolor: alpha(theme.palette.info.main, 0.12) } : undefined,
                  }}
                  onClick={href ? () => navigate(href) : undefined}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {ins.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                    {ins.message}
                  </Typography>
                </Box>
              );
              return body;
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
